import React, { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { DialogDescription, DialogTitle } from 'components/ui/dialog';
import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/RadioGroup';
import { Textarea } from 'components/ui/textarea';
import { ExamDialog } from 'components/exam/ExamDialog';
import { QUESTION_REPORT_COMMENT_MAX_LENGTH, QUESTION_REPORT_REASONS, type QuestionReportReason } from '../constants';
import type { QuestionReport } from '../types';
import { useSaveQuestionReport } from '../useSaveQuestionReport';

export interface ReportQuestionDialogProps {
  open: boolean;
  attemptId: string;
  questionId: string;
  /** The report already sent for this question, when the learner is editing it. */
  initial?: QuestionReport;
  onClose: () => void;
}

/** "Savolda xato bormi?": a reason and an optional note. Nothing here touches the learner's answer or the timer. */
export function ReportQuestionDialog({ open, attemptId, questionId, initial, onClose }: ReportQuestionDialogProps) {
  const { t } = useTranslation();
  const ids = useId();
  const save = useSaveQuestionReport(attemptId, questionId);
  // The form is the dialog's child, so it mounts fresh on every open and starts from the saved report.
  return (
    <ExamDialog open={open} onClose={onClose} widthClass="max-w-[480px]">
      <DialogTitle className="text-[20px] font-bold leading-tight tracking-[-0.02em]">
        {t('question_reports.title')}
      </DialogTitle>
      <DialogDescription className="mt-1.5 text-sm text-muted-foreground">
        {t('question_reports.description')}
      </DialogDescription>
      <ReportForm
        ids={ids}
        initial={initial}
        pending={save.isPending}
        onCancel={onClose}
        onSubmit={async (input) => {
          try {
            await save.mutateAsync(input);
            onClose();
          } catch {
            // The hook has already told the learner; the dialog stays open so nothing they typed is lost.
          }
        }}
      />
    </ExamDialog>
  );
}

interface ReportFormProps {
  ids: string;
  initial?: QuestionReport;
  pending: boolean;
  onCancel: () => void;
  onSubmit: (input: { reason: QuestionReportReason; comment?: string }) => void;
}

function ReportForm({ ids, initial, pending, onCancel, onSubmit }: ReportFormProps) {
  const { t } = useTranslation();
  const [reason, setReason] = useState<QuestionReportReason | undefined>(initial?.reason);
  const [comment, setComment] = useState(initial?.comment ?? '');

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!reason || pending) return;
    const note = comment.trim();
    onSubmit({ reason, ...(note && { comment: note }) });
  };

  return (
    <form onSubmit={submit} className="mt-5 space-y-5" noValidate>
      <RadioGroup
        value={reason ?? ''}
        onValueChange={(next) => setReason(next as QuestionReportReason)}
        aria-label={t('question_reports.reason_label')}
        className="gap-2"
      >
        {QUESTION_REPORT_REASONS.map((option) => (
          <Label
            key={option}
            htmlFor={`${ids}-${option}`}
            className="flex cursor-pointer items-center gap-3 rounded-lg border border-border px-3.5 py-3 text-sm font-medium transition-colors hover:border-foreground/40 has-[[data-state=checked]]:border-foreground"
          >
            <RadioGroupItem id={`${ids}-${option}`} value={option} disabled={pending} />
            {t(`question_reports.reason_${option}`)}
          </Label>
        ))}
      </RadioGroup>

      <div>
        <Textarea
          aria-label={t('question_reports.comment_placeholder')}
          placeholder={t('question_reports.comment_placeholder')}
          value={comment}
          maxLength={QUESTION_REPORT_COMMENT_MAX_LENGTH}
          rows={3}
          disabled={pending}
          onChange={(event) => setComment(event.target.value)}
        />
        <p className="mt-1 text-right text-xs tabular-nums text-muted-foreground" aria-hidden="true">
          {comment.length} / {QUESTION_REPORT_COMMENT_MAX_LENGTH}
        </p>
      </div>

      <div className="flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" disabled={pending} onClick={onCancel}>
          {t('question_reports.cancel')}
        </Button>
        <Button type="submit" disabled={!reason || pending} aria-busy={pending}>
          {t('question_reports.send')}
        </Button>
      </div>
    </form>
  );
}
